import apiRequest from './api-request';

const MAX_ORDER_FILE_BYTES = 25 * 1024 * 1024;

const ALLOWED_ORDER_FILE_MIME_TYPES = new Set([
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]);

const EXTENSION_MIME_TYPES: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

// Extensions only. iOS hides or refuses files when accept also lists long Office MIME types.
export const ORDER_FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.heic,.heif,.doc,.docx,.xls,.xlsx';

export type OrderFile = {
    id: number;
    groupId: string;
    version: number;
    originalFilename: string;
    mimeType: string;
    byteSize: number;
    title: string;
    description: string;
    createdAt: string;
    uploadedByTenantId: number;
    uploadedByTenantName: string;
    uploadedByUserName: string | null;
    olderVersionCount: number;
    deletedAt?: string | null;
};

function fileExtension(name: string) {
    const base = name.split(/[/\\]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
}

/**
 * Phone browsers often leave file.type empty, or send octet-stream, for a known extension.
 * iPhone photos can also arrive as HEIC, which is not an allowed type.
 */
export function orderFileMimeType(file: File) {
    const type = file.type.toLowerCase().split(';')[0].trim();
    if (type === 'image/heic' || type === 'image/heif') {
        return null;
    }
    if (ALLOWED_ORDER_FILE_MIME_TYPES.has(type)) {
        return type;
    }
    const fromExtension = EXTENSION_MIME_TYPES[fileExtension(file.name)];
    if (
        fromExtension
        && (!type || type === 'application/octet-stream' || type === 'image/pjpeg')
    ) {
        return fromExtension;
    }
    return null;
}

export function orderFileClientError(file: File) {
    const type = file.type.toLowerCase().split(';')[0].trim();
    if (type === 'image/heic' || type === 'image/heif') {
        return 'iPhone-bilder i HEIC stöds inte. Spara bilden som JPG eller PNG och försök igen.';
    }
    if (!orderFileMimeType(file)) {
        return 'Filtypen stöds inte. Välj PDF, PNG, JPG, JPEG eller Office (doc, docx, xls, xlsx).';
    }
    if (file.size > MAX_ORDER_FILE_BYTES) {
        return 'Filen får vara högst 25 MB.';
    }
    if (file.size <= 0) {
        return 'Filen är tom.';
    }
    return null;
}

export function listOrderFiles(orderId: number) {
    return apiRequest({
        method: 'GET',
        resource: `orders/${orderId}/files`
    }) as Promise<OrderFile[]>;
}

export function listOrderFileVersions(orderId: number, groupId: string) {
    return apiRequest({
        method: 'GET',
        resource: `orders/${orderId}/files/${groupId}/versions`
    }) as Promise<OrderFile[]>;
}

export function presignOrderFile(orderId: number, body: {
    mimeType: string;
    originalFilename: string;
    title: string;
    description?: string;
    groupId?: string;
}) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/presign`,
        body
    }) as Promise<{ presignedUrl: string; s3Key: string }>;
}

export function confirmOrderFile(orderId: number, body: {
    mimeType: string;
    originalFilename: string;
    title: string;
    description?: string;
    groupId?: string;
    s3Key: string;
}) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/confirm`,
        body
    });
}

export function downloadOrderFile(orderId: number, fileId: number, options?: { inline?: boolean }) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${fileId}/download`,
        body: options?.inline ? { inline: true } : undefined
    }) as Promise<{ presignedUrl: string }>;
}

export function deleteOrderFileGroup(orderId: number, groupId: string) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${groupId}/delete`
    });
}

export function restoreOrderFile(orderId: number, fileId: number) {
    return apiRequest({
        method: 'POST',
        resource: `orders/${orderId}/files/${fileId}/restore`
    });
}
